import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WebshopForm } from '../components/Webshop/WebshopForm';
import useLocalStorageState from 'use-local-storage-state';
import { DEFAULT_LANGUAGE } from '../components/Header/LanguageDropdown';
import { AddonListActions, Product, ProductListActions } from '../types/webshop';
import { Addon, ShopContent } from '../types/cms_shared_types';
import { ProductWithDiscount } from '../types/api_shared_types';
import { apiGetCartPrice, apiGetCmsContent } from '../api/api';
import { handleError } from '../helper/errors';
import { REFERENCE_NUMBER_REGEX } from '../helper/helper';
import { retrieveCmsProduct } from '../helper/cms';

interface WebshopPageProps {
  defaultLanguage?: string;
}

export const WebshopPage: FC<WebshopPageProps> = ({ defaultLanguage }) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [cmsShopContent, setCmsShopContent] = useState<ShopContent | undefined>();

  const [language, setLanguage] = useLocalStorageState<string>('language', {
    defaultValue: defaultLanguage ?? DEFAULT_LANGUAGE,
  });

  useEffect(() => {
    const getCmsContent = async () => {
      try {
        const res = await apiGetCmsContent(language, 'shop');
        setCmsShopContent(res.data as ShopContent);
      } catch (err) {
        handleError(err, navigate);
      }
    };

    getCmsContent();
  }, [language, navigate]);

  useEffect(() => {
    if (defaultLanguage) {
      setLanguage(defaultLanguage);
      navigate('/');
    }
  }, [defaultLanguage, setLanguage, navigate]);

  const [selectedProducts, setSelectedProducts] = useLocalStorageState<Product[]>('selected_products', {
    defaultValue: [],
  });

  const [selectedCategoryKey] = useLocalStorageState<string | undefined>('selected_category_key', {
    defaultValue: undefined,
  });

  const [selectedSubcategoryKey] = useLocalStorageState<string | undefined>('selected_subcategory_key', {
    defaultValue: undefined,
  });

  const fetchCartPrices = async (products: Product[]) => {
    if (products.length === 0) {
      setSelectedProducts([]);
      return;
    }
    try {
      const productsWithDiscount: ProductWithDiscount[] = products.map((product) => {
        const baseProduct: ProductWithDiscount = {
          code: product.code,
          quantity: product.quantity,
          timeQuantity: product.timeQuantity,
          musicClass: product.musicClass,
          priceYearInclVat: product.priceYearInclVat,
          priceYearExclVat: product.priceYearExclVat,
        };

        if (cmsShopContent && selectedCategoryKey && selectedSubcategoryKey) {
          const cmsProduct = retrieveCmsProduct(
            product.code,
            cmsShopContent.categories,
            selectedCategoryKey,
            selectedSubcategoryKey,
          );
          if (cmsProduct) {
            return {
              ...baseProduct,
              triggerDiscount: cmsProduct.triggerDiscount,
              discountReplacementProduct: cmsProduct.discountReplacementProduct,
            };
          }
        }
        return baseProduct;
      });

      const { data } = await apiGetCartPrice({ products: productsWithDiscount });

      const productMap = new Map(products.map((p) => [p.code, p]));

      const updatedProducts: Product[] = data
        .map((productWithDiscount) => {
          const originalProduct = productMap.get(productWithDiscount.code);
          if (!originalProduct) return null;

          return {
            ...originalProduct,
            priceYearInclVat: productWithDiscount.priceYearInclVat,
            priceYearExclVat: productWithDiscount.priceYearExclVat,
          };
        })
        .filter((product): product is Product => product !== null);

      setSelectedProducts(updatedProducts);
    } catch (err) {
      handleError(err, navigate);
    }
  };

  const addProduct = (newProduct: Product) => {
    return fetchCartPrices([...selectedProducts, newProduct]);
  };

  const addMultipleProducts = (newProducts: Product[]) => {
    return fetchCartPrices([...selectedProducts, ...newProducts]);
  };

  const replaceProduct = (newProduct: Product) => {
    const index = selectedProducts.findIndex((p) => p.code === newProduct.code);
    return fetchCartPrices([...selectedProducts.slice(0, index), newProduct, ...selectedProducts.slice(index + 1)]);
  };

  const removeProduct = (index: number) => {
    return fetchCartPrices([...selectedProducts.slice(0, index), ...selectedProducts.slice(index + 1)]);
  };

  const removeProductsByCodes = (codes: string[]) => {
    return fetchCartPrices(selectedProducts.filter((p) => !codes.includes(p.code)));
  };

  const resetProducts = () => setSelectedProducts([]);

  const selectedProductsActions: ProductListActions = {
    add: addProduct,
    addMultiple: addMultipleProducts,
    replace: replaceProduct,
    remove: removeProduct,
    removeByCodes: removeProductsByCodes,
    reset: resetProducts,
  };

  const [selectedAddons, setSelectedAddons] = useLocalStorageState<Addon[]>('selected_addons', {
    defaultValue: [],
  });

  const addAddon = (newAddon: Addon) => {
    setSelectedAddons((prev) => [...prev, newAddon]);
  };

  const replaceAddon = (oldAddon: Addon, newAddon: Addon) => {
    const index = selectedAddons.findIndex((addon) => addon.code === oldAddon.code);
    setSelectedAddons((prev) => [...prev.slice(0, index), newAddon, ...prev.slice(index + 1)]);
  };

  const removeAddon = (index: number) => {
    setSelectedAddons((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const resetAddons = () => setSelectedAddons([]);

  const selectedAddonsActions: AddonListActions = {
    add: addAddon,
    replace: replaceAddon,
    remove: removeAddon,
    reset: resetAddons,
  };

  // Handle reference number
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const paramReferenceNum = searchParams.get('referenceNum') || searchParams.get('referenceNumber');
  useEffect(() => {
    if (paramReferenceNum && REFERENCE_NUMBER_REGEX.test(paramReferenceNum)) {
      setReferenceNumber(paramReferenceNum);
    } else {
      setReferenceNumber(null);
    }
  }, [paramReferenceNum, setReferenceNumber]);

  if (!cmsShopContent) {
    return <></>;
  }

  return (
    <WebshopForm
      selectedProducts={selectedProducts}
      selectedProductsActions={selectedProductsActions}
      selectedAddons={selectedAddons}
      selectedAddonsActions={selectedAddonsActions}
      cmsShopContent={cmsShopContent}
      referenceNumber={referenceNumber}
    />
  );
};
